import { AgentBackendSchema, AgentProgramSchema } from '@developerz/domain';
import type { JSX } from 'solid-js';
import { t } from '../i18n';
import type { TemplateFormController } from '../lib/template-form';
import { Field } from './Field';
import { Select, type SelectOption } from './Select';

// The agent-template field set. Pure presenter over `createTemplateForm` — the
// modal owns the shell and the submit.
//
// The program + backend vocabularies come off the DOMAIN enums the API parses
// with, never a hand-typed list: a select offering a value the route rejects (or
// hiding one it accepts) is exactly the drift this avoids. Both are the themed
// `Select` (#5631); a row's label is the enum value itself, an identifier the
// operator types into an MCP call, so there is no copy to translate.
//
// The name is disabled on an edit because it is the ADDRESS (`PUT
// /v1/templates/:name`) — a rename is a create plus a delete on every surface,
// and an editable box here would silently fork a second template instead.

const PROGRAM_OPTIONS: readonly SelectOption<string>[] = AgentProgramSchema.options.map(
  (program) => ({ value: program, label: program }),
);
const BACKEND_OPTIONS: readonly SelectOption<string>[] = AgentBackendSchema.options.map(
  (backend) => ({ value: backend, label: backend }),
);

export function TemplateFields(props: {
  readonly form: TemplateFormController;
  /** True when editing an existing template (the name is then its address). */
  readonly editing: boolean;
}): JSX.Element {
  return (
    <div class="field-group">
      <Field
        label={t('account_templates.form_name')}
        id="template-name"
        desc={props.editing ? t('account_templates.name_immutable') : undefined}
      >
        {(a11y) => (
          <input
            {...a11y}
            class="input"
            type="text"
            value={props.form.name()}
            disabled={props.editing}
            onInput={(e) => props.form.setName(e.currentTarget.value)}
            required
          />
        )}
      </Field>

      <Field label={t('account_templates.form_program')} id="template-program">
        {(a11y) => (
          <Select<string>
            a11y={a11y}
            label={t('account_templates.form_program')}
            block
            value={props.form.program()}
            options={PROGRAM_OPTIONS}
            onChange={props.form.setProgram}
          />
        )}
      </Field>

      <Field label={t('account_templates.form_backend')} id="template-backend">
        {(a11y) => (
          <Select<string>
            a11y={a11y}
            label={t('account_templates.form_backend')}
            block
            value={props.form.backend()}
            options={BACKEND_OPTIONS}
            onChange={props.form.setBackend}
          />
        )}
      </Field>

      {/* The base-URL hint is a HINT, not a gate: the pairing is refused
          server-side (422) so all three doors answer the same sentence. */}
      <Field
        label={t('account_templates.form_base_url')}
        id="template-base-url"
        desc={t(
          props.form.needsBaseUrl()
            ? 'account_templates.base_url_required'
            : 'account_templates.base_url_desc',
        )}
      >
        {(a11y) => (
          <input
            {...a11y}
            class="input"
            type="url"
            value={props.form.baseUrl()}
            onInput={(e) => props.form.setBaseUrl(e.currentTarget.value)}
          />
        )}
      </Field>

      <Field label={t('account_templates.form_model')} id="template-model">
        {(a11y) => (
          <input
            {...a11y}
            class="input"
            type="text"
            value={props.form.model()}
            onInput={(e) => props.form.setModel(e.currentTarget.value)}
          />
        )}
      </Field>

      <Field label={t('account_templates.form_kb_ref')} id="template-kb-ref">
        {(a11y) => (
          <input
            {...a11y}
            class="input"
            type="text"
            value={props.form.kbRef()}
            onInput={(e) => props.form.setKbRef(e.currentTarget.value)}
          />
        )}
      </Field>

      <Field label={t('account_templates.form_system_prompt')} id="template-system-prompt">
        {(a11y) => (
          <textarea
            {...a11y}
            class="input"
            rows="4"
            value={props.form.systemPrompt()}
            onInput={(e) => props.form.setSystemPrompt(e.currentTarget.value)}
          />
        )}
      </Field>

      <Field
        label={t('account_templates.form_invocation')}
        id="template-invocation"
        desc={t('account_templates.invocation_desc')}
        error={props.form.invocationError()}
      >
        {(a11y) => (
          <textarea
            {...a11y}
            class="input"
            rows="5"
            value={props.form.invocation()}
            onInput={(e) => props.form.setInvocation(e.currentTarget.value)}
          />
        )}
      </Field>
    </div>
  );
}
